const SVG_MIME = 'image/svg+xml';

const XHTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';

// The elements that can contain a script or HTML, or that can set an attribute at
// runtime. This includes the SMIL animation tags.
const STRIPPED_TAGS = new Set([
  'script',
  'foreignobject',
  'animate',
  'animatemotion',
  'animatetransform',
  'set',
  'handler',
]);

/** A reference to a part of the drawing itself, or to an image in the drawing. */
const SAFE_REFERENCE = /^(#|data:image\/)/i;

const AT_IMPORT = /@import[^;]*;?/gi;

/** Returns true for an event handler, or a link that points out of the drawing. */
function isUnsafe(attribute: Attr): boolean {
  const name = attribute.localName.toLowerCase();
  if (name.startsWith('on')) return true;
  if (name !== 'href') return false;
  return !SAFE_REFERENCE.test(attribute.value.trim());
}

/** Returns true for an element that can run a script or hold HTML, in any namespace. */
function isStripped(element: Element): boolean {
  return element.namespaceURI === XHTML_NAMESPACE || STRIPPED_TAGS.has(element.localName.toLowerCase());
}

/** Removes the unsafe attributes and elements under one element. */
function scrub(node: Element): void {
  for (const attribute of Array.from(node.attributes)) {
    if (isUnsafe(attribute)) node.removeAttributeNode(attribute);
  }

  for (const child of Array.from(node.children)) {
    if (isStripped(child)) {
      child.remove();
      continue;
    }
    if (child.localName.toLowerCase() === 'style') {
      child.textContent = (child.textContent ?? '').replace(AT_IMPORT, '');
    }
    scrub(child);
  }
}

/** Returns one drawing with its scripts, event handlers, `@import` rules and
 *  outward references removed; throws when the markup will not parse. */
export function sanitizeSvgMarkup(markup: string): string {
  const parsed = new DOMParser().parseFromString(markup, SVG_MIME);
  const root = parsed.documentElement;
  if (!root || root.localName !== 'svg' || root.querySelector('parsererror')) {
    throw new Error('That file is not a drawing this editor can read.');
  }

  scrub(root);
  return new XMLSerializer().serializeToString(root);
}

/** Returns the text of one file. */
export function textOf(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('That file could not be read.'));
    reader.readAsText(file);
  });
}

/** Returns true when the type or the extension of the file is SVG. */
export function isSvgFile(file: File): boolean {
  return file.type === SVG_MIME || file.name.toLowerCase().endsWith('.svg');
}

/** Returns the same file with its markup sanitized, with its name and type. */
export async function sanitizeSvgFile(file: File): Promise<File> {
  const markup = sanitizeSvgMarkup(await textOf(file));
  return new File([markup], file.name, { type: SVG_MIME });
}

/** One media type and the function that cleans a file of that type. */
export interface UploadRule {
  name: string;
  claims: (file: File) => boolean;
  clean: (file: File) => Promise<File>;
}

// The rules an upload is put through, first match wins.
export const UPLOAD_RULES: UploadRule[] = [
  { name: 'svg', claims: isSvgFile, clean: sanitizeSvgFile },
];

/** Returns the file cleaned by the first rule that claims it, or the file as it is. */
export function sanitizeUpload(file: File, rules: UploadRule[] = UPLOAD_RULES): Promise<File> {
  const rule = rules.find((candidate) => candidate.claims(file));
  return rule ? rule.clean(file) : Promise.resolve(file);
}

/** Returns a copy of the form with each file passed through `sanitizeUpload`. */
export async function sanitizeForm(form: FormData): Promise<FormData> {
  const clean = new FormData();
  for (const [key, value] of form) clean.append(key, value instanceof File ? await sanitizeUpload(value) : value);
  return clean;
}

/** Returns true when the value is a full http or https address with a host name that has a dot. */
export function isWebUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.');
  } catch {
    return false;
  }
}
